import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  AppBar,
} from '@mui/material';
import {
  Search,
  FilterList,
  PictureAsPdf,
  Visibility,
  Edit,
  Delete,
  Timeline,
  Compare,
  GetApp,
  DateRange,
  Engineering,
  Assessment,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { maintenanceRecordService } from '../../services/maintenanceService';
import { MaintenanceRecord } from '../../types/maintenance';

interface MaintenanceRecordsProps {}

interface FilterState {
  search: string;
  maintenance_task: string;
  completion_status: string;
  completed_by: string;
  completed_at_from: Date | null;
  completed_at_to: Date | null;
  equipment: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`maintenance-records-tabpanel-${index}`}
      aria-labelledby={`maintenance-records-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const MaintenanceRecords: React.FC<MaintenanceRecordsProps> = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRecords, setSelectedRecords] = useState<number[]>([]);
  
  // Filter states
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    maintenance_task: '',
    completion_status: '',
    completed_by: '',
    completed_at_from: null,
    completed_at_to: null,
    equipment: '',
  });
  
  // Dialog states
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [recordDetailOpen, setRecordDetailOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<MaintenanceRecord | null>(null);
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const apiFilters: any = {
        page: page + 1,
        page_size: rowsPerPage,
        ordering: '-completed_at',
      };

      // Add non-empty filters
      if (filters.search) apiFilters.search = filters.search;
      if (filters.maintenance_task) apiFilters.maintenance_task = filters.maintenance_task;
      if (filters.completion_status) apiFilters.completion_status = filters.completion_status;
      if (filters.completed_by) apiFilters.completed_by = filters.completed_by;
      if (filters.equipment) apiFilters.equipment = filters.equipment;
      if (filters.completed_at_from) {
              apiFilters.completed_at_from = dayjs(filters.completed_at_from).format('YYYY-MM-DD');
    }
    if (filters.completed_at_to) {
      apiFilters.completed_at_to = dayjs(filters.completed_at_to).format('YYYY-MM-DD');
      }

      const response = await maintenanceRecordService.getRecords(apiFilters);
      setRecords(response.results);
      setTotalCount(response.count);
      setError(null);
    } catch (err) {
      console.error('Failed to load maintenance records:', err);
      setError('Failed to load maintenance records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const handleRecordSelect = (recordId: number) => {
    setSelectedRecords(prev => 
      prev.includes(recordId) 
        ? prev.filter(id => id !== recordId)
        : [...prev, recordId]
    );
  };

  const handleSelectAllRecords = () => {
    if (selectedRecords.length === records.length) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(records.map(r => r.id));
    }
  };

  const handleViewRecord = (record: MaintenanceRecord) => {
    setSelectedRecord(record);
    setRecordDetailOpen(true);
  };

  const handleGeneratePDF = async (recordId: number) => {
    try {
      const blob = await maintenanceRecordService.generateRecordPDF(recordId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maintenance_record_${recordId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      alert('Failed to generate PDF');
    }
  };

  const handleGenerateComparisonPDF = async () => {
    if (selectedRecords.length === 0) {
      alert('Please select records to compare');
      return;
    }

    try {
      const blob = await maintenanceRecordService.generateComparisonPDF(selectedRecords);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maintenance_comparison_${selectedRecords.length}_records.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to generate comparison PDF:', err);
      alert('Failed to generate comparison PDF');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'partial':
        return 'warning';
      case 'draft':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Maintenance Records
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review historical maintenance records, generate reports, and analyze data trends
          </Typography>
        </Box>

        {/* Action Bar */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search records..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
            }}
            sx={{ minWidth: 300 }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterDialogOpen(true)}
          >
            Filters
          </Button>

          {selectedRecords.length > 0 && (
            <>
              <Button
                variant="contained"
                startIcon={<Compare />}
                onClick={handleGenerateComparisonPDF}
                color="primary"
              >
                Compare Selected ({selectedRecords.length})
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Timeline />}
                onClick={() => setComparisonDialogOpen(true)}
              >
                Analyze Data
              </Button>
            </>
          )}
        </Box>

        {/* Content Tabs */}
        <Card>
          <AppBar position="static" color="default" elevation={0}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Records List" icon={<Assessment />} />
              <Tab label="Data Analysis" icon={<Timeline />} />
            </Tabs>
          </AppBar>

          <TabPanel value={activeTab} index={0}>
            {/* Records Table */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedRecords.length === records.length && records.length > 0}
                        indeterminate={selectedRecords.length > 0 && selectedRecords.length < records.length}
                        onChange={handleSelectAllRecords}
                      />
                    </TableCell>
                    <TableCell>Task</TableCell>
                    <TableCell>Equipment</TableCell>
                    <TableCell>Completed By</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Completed Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No maintenance records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((record) => (
                      <TableRow key={record.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleRecordSelect(record.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {record.maintenance_task?.task_name || 'Unknown Task'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Work Order: {typeof record.work_order === 'object' ? record.work_order?.work_order_number : record.work_order || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {record.equipment_name || 'Unknown Equipment'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.equipment_code || 'No Code'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {record.completed_by_detail?.first_name} {record.completed_by_detail?.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {record.completed_by_detail?.username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={record.completion_status}
                            size="small"
                            color={getStatusColor(record.completion_status) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {dayjs(record.completed_at).format('MMM DD, YYYY')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {dayjs(record.completed_at).format('HH:mm')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewRecord(record)}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Generate PDF">
                              <IconButton
                                size="small"
                                onClick={() => handleGeneratePDF(record.id)}
                              >
                                <PictureAsPdf />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Record">
                              <IconButton size="small">
                                <Edit />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {/* Data Analysis Tab - Placeholder for now */}
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Data Analysis
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Select maintenance records to analyze measurement data over time
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setActiveTab(0)}
              >
                Go to Records to Select Data
              </Button>
            </Box>
          </TabPanel>
        </Card>

        {/* Filter Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Filter Maintenance Records</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Completion Status</InputLabel>
                  <Select
                    value={filters.completion_status}
                    label="Completion Status"
                    onChange={(e) => handleFilterChange('completion_status', e.target.value)}
                  >
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Completed From"
                  value={filters.completed_at_from}
                  onChange={(value) => handleFilterChange('completed_at_from', value)}
                  slots={{
                    textField: TextField
                  }}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Completed To"
                  value={filters.completed_at_to}
                  onChange={(value) => handleFilterChange('completed_at_to', value)}
                  slots={{
                    textField: TextField
                  }}
                  slotProps={{
                    textField: { fullWidth: true }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                setFilters({
                  search: '',
                  maintenance_task: '',
                  completion_status: '',
                  completed_by: '',
                  completed_at_from: null,
                  completed_at_to: null,
                  equipment: '',
                });
                setFilterDialogOpen(false);
              }}
              color="secondary"
            >
              Clear All
            </Button>
            <Button
              onClick={() => setFilterDialogOpen(false)}
              variant="contained"
            >
              Apply Filters
            </Button>
          </DialogActions>
        </Dialog>

        {/* Record Detail Dialog */}
        <Dialog
          open={recordDetailOpen}
          onClose={() => setRecordDetailOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Maintenance Record Details
            {selectedRecord && (
              <Button
                startIcon={<PictureAsPdf />}
                onClick={() => handleGeneratePDF(selectedRecord.id)}
                sx={{ ml: 2 }}
              >
                Generate PDF
              </Button>
            )}
          </DialogTitle>
          <DialogContent>
            {selectedRecord && (
              <Box sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Grid container spacing={3}>
                  {/* Basic Information Section */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 2, mb: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        Basic Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography><strong>Record ID:</strong> {selectedRecord.id}</Typography>
                        <Typography><strong>Task:</strong> {selectedRecord.maintenance_task?.task_name || 'N/A'}</Typography>
                        <Typography><strong>Equipment:</strong> {selectedRecord.equipment_name}</Typography>
                        <Typography><strong>Equipment Code:</strong> {selectedRecord.equipment_code}</Typography>
                        <Typography><strong>Status:</strong> 
                          <Chip 
                            label={selectedRecord.completion_status_display || selectedRecord.completion_status} 
                            size="small" 
                            color={getStatusColor(selectedRecord.completion_status) as any}
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                        <Typography><strong>Completed By:</strong> 
                          {selectedRecord.completed_by_detail?.first_name} {selectedRecord.completed_by_detail?.last_name}
                          {selectedRecord.completed_by_detail?.username && ` (${selectedRecord.completed_by_detail.username})`}
                        </Typography>
                        <Typography><strong>Completed At:</strong> {dayjs(selectedRecord.completed_at).format('PPpp')}</Typography>
                        {selectedRecord.work_order && (
                          <Typography><strong>Work Order:</strong> {typeof selectedRecord.work_order === 'object' ? selectedRecord.work_order?.work_order_number : selectedRecord.work_order}</Typography>
                        )}
                      </Box>
                    </Card>
                  </Grid>

                  {/* Timestamps Section */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ p: 2, mb: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        Timestamps
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography><strong>Completed:</strong> {dayjs(selectedRecord.completed_at).format('MMM dd, yyyy HH:mm')}</Typography>
                        <Typography><strong>Record Created:</strong> {dayjs(selectedRecord.created_at).format('MMM dd, yyyy HH:mm')}</Typography>
                        <Typography><strong>Last Updated:</strong> {dayjs(selectedRecord.updated_at).format('MMM dd, yyyy HH:mm')}</Typography>
                      </Box>
                    </Card>
                  </Grid>

                  {/* Work Details Section */}
                  <Grid item xs={12}>
                    <Card sx={{ p: 2, mb: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        Work Details
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Work Performed:</Typography>
                        <Typography 
                          paragraph 
                          sx={{ 
                            backgroundColor: '#f5f5f5', 
                            p: 2, 
                            borderRadius: 1,
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'monospace'
                          }}
                        >
                          {selectedRecord.work_performed || 'No work description provided'}
                        </Typography>
                      </Box>

                      {selectedRecord.findings && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Findings:</Typography>
                          <Typography 
                            paragraph 
                            sx={{ 
                              backgroundColor: '#f0f8ff', 
                              p: 2, 
                              borderRadius: 1,
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {selectedRecord.findings}
                          </Typography>
                        </Box>
                      )}

                      {selectedRecord.issues_discovered && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Issues Discovered:</Typography>
                          <Typography 
                            paragraph 
                            sx={{ 
                              backgroundColor: '#fff5f5', 
                              p: 2, 
                              borderRadius: 1,
                              border: '1px solid #ffcdd2',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {selectedRecord.issues_discovered}
                          </Typography>
                        </Box>
                      )}

                      {selectedRecord.parts_replaced && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Parts Replaced:</Typography>
                          <Typography 
                            paragraph 
                            sx={{ 
                              backgroundColor: '#f0fff0', 
                              p: 2, 
                              borderRadius: 1,
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {selectedRecord.parts_replaced}
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  </Grid>

                  {/* Follow-up Section */}
                  {(selectedRecord.follow_up_required || selectedRecord.follow_up_notes) && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" gutterBottom color="warning.main">
                          Follow-up Required
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <Typography><strong>Follow-up Required:</strong> 
                            <Chip 
                              label={selectedRecord.follow_up_required ? 'Yes' : 'No'} 
                              size="small" 
                              color={selectedRecord.follow_up_required ? 'warning' : 'default'}
                              sx={{ ml: 1 }}
                            />
                          </Typography>
                          {selectedRecord.follow_up_notes && (
                            <Box>
                              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Follow-up Notes:</Typography>
                              <Typography 
                                sx={{ 
                                  backgroundColor: '#fff9c4', 
                                  p: 2, 
                                  borderRadius: 1,
                                  whiteSpace: 'pre-wrap'
                                }}
                              >
                                {selectedRecord.follow_up_notes}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  )}

                  {/* Task Data Responses Section */}
                  {selectedRecord.data_responses && selectedRecord.data_responses.length > 0 && (
                    <Grid item xs={12}>
                      <Card sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                          Task Data Responses
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Field</strong></TableCell>
                              <TableCell><strong>Response</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell><strong>Recorded At</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedRecord.data_responses.map((response: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{response.data_field_label}</TableCell>
                                <TableCell>
                                  {response.display_value || response.response_value || 'No response'}
                                  {response.unit_of_measure && ` ${response.unit_of_measure}`}
                                </TableCell>
                                <TableCell>
                                  <Chip label={response.input_type} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  {response.responded_at ? dayjs(response.responded_at).format('MMM dd, HH:mm') : 'N/A'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Card>
                    </Grid>
                  )}

                  {/* System Information */}
                  <Grid item xs={12}>
                    <Card sx={{ p: 2, backgroundColor: '#fafafa' }}>
                      <Typography variant="h6" gutterBottom color="text.secondary">
                        System Information
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Record ID</Typography>
                          <Typography variant="body2">{selectedRecord.id}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">Task ID</Typography>
                          <Typography variant="body2">{selectedRecord.maintenance_task?.id || 'N/A'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary">User ID</Typography>
                          <Typography variant="body2">{selectedRecord.completed_by}</Typography>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecordDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default MaintenanceRecords; 